import React, { useEffect, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Container,
    IconButton,
    Box,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from './AxiosConfig';
import Loading from "../Loading";

const paymentMethodMapping = {
    WAITER_CARD: 'Płatność kartą',
    WAITER_CASH: 'Płatność gotówką',
};

const paymentStatusMapping = {
    PAID: 'Opłacone',
    NEW: 'Nieopłacone',
};

const OrdersTable = ({ token, baseUrl, onBack }) => {
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true);
            try {
                const response = await api.get(`${baseUrl}/api/restaurant/getCompletedOrders`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setOrders(response.data);
            } catch (error) {
                alert('Błąd przy pobieraniu historii zamówień');
            } finally {
                setLoading(false)
            }
        };

        fetchOrders();
    }, [token, baseUrl]);

    const columns = [
        { accessorKey: 'id', header: 'ID' },
        {
            accessorKey: 'createdAt',
            header: 'Data utworzenia',
            Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString('pl-PL'),
            filterFn: (row, columnId, filterValue) => {
                const cellValue = row.getValue(columnId);
                if (!cellValue) return false;
                const cellDate = new Date(cellValue);
                const startDate = filterValue[0] ? new Date(filterValue[0]) : null;
                const endDate = filterValue[1] ? new Date(filterValue[1]) : null;
                if (startDate && cellDate < startDate) return false;
                if (endDate && cellDate > endDate) return false;
                return true;
            },
            Filter: ({ column }) => {
                const filterValue = column.getFilterValue() || ['', ''];
                return (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <input
                                type="datetime-local"
                                value={filterValue[0]}
                                onChange={(e) => {
                                    column.setFilterValue([e.target.value, filterValue[1]]);
                                }}
                            />
                            <input
                                type="datetime-local"
                                value={filterValue[1]}
                                onChange={(e) => {
                                    column.setFilterValue([filterValue[0], e.target.value]);
                                }}
                            />
                        </div>
                    </div>
                );
            },
        },
        { accessorKey: 'tableNumber', header: 'Numer stolika' },
        {
            accessorFn: (row) => row.orderItems.length,
            header: 'Liczba pozycji',
        },
        {
            accessorFn: (row) => row.payments.reduce((sum, p) => sum + p.totalAmount, 0).toFixed(2),
            header: 'Kwota łącznie (zł)',
            filterVariant: 'range',
        },
    ];

    const table = useMaterialReactTable({
        columns,
        data: orders,
        initialState: {
            sorting: [{ id: 'id', desc: true }],
        },
        enableSorting: true,
        enableFiltering: true,
        localization: {
            actions: 'Akcje',
            cancel: 'Anuluj',
            clearFilter: 'Wyczyść filtr',
            clearSearch: 'Wyczyść wyszukiwanie',
            filterByColumn: 'Filtruj według kolumny',
            filterMode: 'Tryb filtrowania',
            hideColumn: 'Ukryj kolumnę',
            noRecordsToDisplay: 'Brak danych do wyświetlenia',
            search: 'Szukaj',
            selectedCountOfRowCountRowsSelected: '{selectedCount} z {rowCount} wierszy zaznaczonych',
            showAll: 'Pokaż wszystko',
            showHideColumns: 'Pokaż/Ukryj kolumny',
            showHideFilters: 'Pokaż/Ukryj filtry',
            showHideSearch: 'Pokaż/Ukryj wyszukiwanie',
            sortByColumnAsc: 'Sortuj rosnąco według {column}',
            sortByColumnDesc: 'Sortuj malejąco według {column}',
            toggleDensity: 'Przełącz gęstość',
            toggleFullScreen: 'Przełącz tryb pełnoekranowy',
            toggleSelectAll: 'Zaznacz/Odznacz wszystko',
            rowsPerPage: 'Wierszy na stronę',
            rangeFrom: 'Zakres od',
            rangeTo: 'Zakres do',
        },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => setSelectedOrder(row.original),
            sx: { cursor: 'pointer' },
        }),
    });

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            <Loading open={loading} />
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4">Historia Zamówień</Typography>
            </Box>

            <MaterialReactTable table={table} />

            {selectedOrder && (
                <Dialog open onClose={() => setSelectedOrder(null)} maxWidth="md" fullWidth>
                    <DialogTitle>Szczegóły zamówienia #{selectedOrder.id}</DialogTitle>
                    <DialogContent dividers>
                        <Box mb={2}>
                            <Typography variant="subtitle1" gutterBottom>
                                Pozycje zamówienia:
                            </Typography>
                            {selectedOrder.orderItems.map((item) => (
                                <Box key={item.id} mb={1}>
                                    <Typography variant="body2">
                                        {item.quantity} x {item.name} – {item.totalPrice} zł
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        {selectedOrder.comments && selectedOrder.comments.length > 0 && (
                            <Box mt={4}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Uwagi:
                                </Typography>
                                {selectedOrder.comments.map((comment, index) => (
                                    <Box
                                        key={index}
                                        mb={2}
                                        p={2}
                                        sx={{
                                            backgroundColor: 'grey.100',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {comment}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Płatności:
                            </Typography>
                            {selectedOrder.payments.map((payment) => (
                                <Box
                                    key={payment.id}
                                    mb={2}
                                    p={2}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'grey.300',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography variant="body2">
                                        <strong>Metoda:</strong> {paymentMethodMapping[payment.paymentMethod] || payment.paymentMethod}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Status:</strong> {paymentStatusMapping[payment.status] || payment.status}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Kwota:</strong> {payment.totalAmount} zł
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Napiwek dla kelnera:</strong> {payment.tipForWaiter} zł
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Faktura:</strong> {payment.isNip ? 'Tak' : 'Nie'}
                                    </Typography>
                                    {payment.isNip && payment.nip && (
                                        <Typography variant="body2">
                                            <strong>NIP:</strong> {payment.nip}
                                        </Typography>
                                    )}
                                    <Box mt={1}>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Pozycje w płatności:</strong>
                                        </Typography>
                                        {payment.items.map((pItem) => (
                                            <Box key={pItem.id} ml={2} mb={0.5}>
                                                <Typography variant="body2">
                                                    {pItem.quantity} x {pItem.name} – {pItem.totalPrice} zł
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedOrder(null)} color="primary">
                            Zamknij
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Container>
    );
};

export default OrdersTable;